import { Component, input, forwardRef, signal } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { InputLabelComponent } from 'src/app/ui-kit/form-controls/input-label/input-label.component';

/**
 * Yes/No Toggle Component
 *
 * A toggle-style input with two buttons for boolean values. Defaults to
 * "No"/"Yes" labels but can render any pair (e.g. "Off"/"On") via the
 * `falseLabel` / `trueLabel` inputs. The selected option is highlighted.
 *
 * @example
 * <app-yes-no-toggle
 *   formControlName="isCompleted"
 *   label="Completed?"
 * ></app-yes-no-toggle>
 *
 * @example
 * <app-yes-no-toggle
 *   [(ngModel)]="enabled"
 *   trueLabel="On"
 *   falseLabel="Off"
 * ></app-yes-no-toggle>
 *
 * Disable it the usual forms way (`[disabled]` alongside `ngModel`, or
 * `control.disable()`): both buttons become inert and use the disabled tokens.
 */
@Component({
  selector: 'app-yes-no-toggle',
  standalone: true,
  imports: [CommonModule, InputLabelComponent],
  host: { class: 'block' },
  template: `
    <!-- Single flex row aligns the optional label with the No/Yes buttons.
         The label takes the leading space; the buttons sit at the trailing
         edge. Buttons are content-sized via min-w; no flex-1 stretch. -->
    <div class="flex items-center gap-3">
      @if (label()) {
        <app-input-label class="flex-1 m-0">{{ label() }}</app-input-label>
      }
      <div class="flex items-center gap-3">
        <!-- No Button -->
        <button
          type="button"
          class="px-3 py-2 rounded-xl min-w-20 text-sm font-semibold theme-border theme-shadow"
          [class.bg-danger]="value === false && !disabled()"
          [class.text-light]="value === false && !disabled()"
          [class.bg-light]="value !== false && !disabled()"
          [class.text-medium]="value !== false && !disabled()"
          [class.bg-surface-disabled]="disabled()"
          [class.text-disabled]="disabled()"
          [class.underline]="disabled() && value === false"
          [disabled]="disabled()"
          [attr.aria-pressed]="value === false"
          (click)="setValue(false)"
        >
          {{ falseLabel() }}
        </button>

        <!-- Yes Button -->
        <button
          type="button"
          class="px-3 py-2 rounded-xl min-w-20 text-sm font-semibold transition-all theme-border theme-shadow"
          [class.bg-success]="value === true && !disabled()"
          [class.bg-light]="value !== true && !disabled()"
          [class.text-medium]="value !== true && !disabled()"
          [class.bg-surface-disabled]="disabled()"
          [class.text-disabled]="disabled()"
          [class.underline]="disabled() && value === true"
          [disabled]="disabled()"
          [attr.aria-pressed]="value === true"
          (click)="setValue(true)"
        >
          {{ trueLabel() }}
        </button>
      </div>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => YesNoToggleComponent),
      multi: true,
    },
  ],
})
export class YesNoToggleComponent implements ControlValueAccessor {
  readonly label = input<string | undefined>(undefined);
  /** Text shown on the truthy button. Defaults to 'Yes'. */
  readonly trueLabel = input<string>('Yes');
  /** Text shown on the falsy button. Defaults to 'No'. */
  readonly falseLabel = input<string>('No');

  value: boolean | null = null;
  /** Set through the forms API (`setDisabledState`). */
  protected readonly disabled = signal(false);

  // ControlValueAccessor
  onChange: (value: boolean | null) => void = () => {};
  onTouched: () => void = () => {};

  writeValue(value: boolean | null): void {
    this.value = value;
  }

  registerOnChange(fn: (value: boolean | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  setValue(value: boolean): void {
    if (this.disabled()) return;
    this.value = value;
    this.onChange(value);
    this.onTouched();
  }
}
